/**
 * Events this browser created in the last half hour, so the create form can
 * catch a host making the same event again.
 *
 * 2026-10-20: a host made "Bheemesh and Geetha wedding", then "Bheemesh and
 * Geetha" and "Bheemesh and Geetha's wedding" within four minutes, tweaking the
 * name and end time each time — Create used as Edit. Her photo landed in the
 * first, her guests in the third. Before creating, the form now asks whether
 * she meant to edit the one she already has.
 *
 * Local on purpose: the case is the same host, minutes apart, on the same
 * device, and it needs no new endpoint. Keyed by the Firebase uid, so a second
 * account on the same browser never sees the first one's events.
 */

const KEY = 'recentCreatedEvents';
/** How far back a "you already have this" can reach. */
const WINDOW_MS = 30 * 60 * 1000;
const MAX_KEPT = 5;

export interface RecentCreate {
  uid: string;
  eventId: string;
  /** What the editor opens the event by. */
  viewerHex: string;
  name: string;
  /** The form's own values, compared as typed: 'yyyy-MM-dd' and its time slot label. */
  startDate: string;
  startTime: string;
  /** Epoch ms of the create. */
  at: number;
}

function readAll(): RecentCreate[] {
  try {
    const list = JSON.parse(localStorage.getItem(KEY) || '[]');
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

function writeAll(list: RecentCreate[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(list));
  } catch {
    // Storage full or blocked: the check just won't fire.
  }
}

export function rememberCreate(entry: RecentCreate): void {
  const fresh = readAll().filter(e => Date.now() - e.at < WINDOW_MS && e.eventId !== entry.eventId);
  writeAll([entry, ...fresh].slice(0, MAX_KEPT));
}

/** Lowercase words, possessives and filler dropped: "Geetha's Wedding!" → geetha, wedding. */
function nameWords(name: string): Set<string> {
  const filler = new Set(['and', 'the', 'of', 'a', 'an', 'for', 'to', 'with']);
  return new Set(
    (name || '')
      .toLowerCase()
      .replace(/['’]s\b/g, '')
      .split(/[^\p{L}\p{N}]+/u)
      .filter(w => w && !filler.has(w)),
  );
}

/**
 * The same event by name: every word of the shorter name is in the longer one
 * ("Bheemesh and Geetha" ⊂ "Bheemesh and Geetha's wedding"). Two different
 * people's birthdays share only "birthday", so they don't match.
 */
export function similarNames(a: string, b: string): boolean {
  const wa = nameWords(a);
  const wb = nameWords(b);
  const [small, large] = wa.size <= wb.size ? [wa, wb] : [wb, wa];
  if (!small.size) return false;
  let shared = 0;
  small.forEach(w => { if (large.has(w)) shared++; });
  return shared / small.size >= 0.8;
}

/**
 * The most recent event this account made in the last half hour that looks
 * like the one about to be created: a similar name, or the exact same start.
 *
 * Same start, not just the same day: a wedding is often a haldi, a mehendi and
 * a sangeet on one date, and each is its own event on purpose.
 */
export function findRecentDuplicate(
  uid: string, name: string, startDate: string, startTime: string,
): RecentCreate | null {
  if (!uid) return null;
  return readAll().find(e =>
    e.uid === uid
    && Date.now() - e.at < WINDOW_MS
    && (similarNames(e.name, name) || (e.startDate === startDate && e.startTime === startTime)),
  ) ?? null;
}
